import React, { useCallback } from 'react';
import { useChartContext } from '../../ChartContext';
import { CHART_TYPES } from '../../constants/chartTypes';
import { useCurrentUrl } from '../../../../utils/routing';
import { useAppDispatch } from '../../../../redux/hooks';
import {
  AnalysisStatus,
  PermissionKey,
} from '../../../../utils/apollo/graphqlSchema';
import usePermission from '../../../../utils/hooks/permissionHooks/usePermission';
import { ModalSliceType, openModal } from '../../../../redux/slices/modal';
import { useShowWorkspace } from '../../../../utils/hooks';
import PermissionElementWrapper from '../../../../pages/PermissionControls/PermissionElementWrapper';
import { SecondaryButton } from '../../../Button';
import { SvgIcon } from '../../../Icon';
import {
  SingleSelect,
  SingleSelectOption,
} from '../../../Dropdown/SingleSelect';
import KubitTooltip from '../../../KubitTooltip';

interface IProps {
  analysisId: string;
  isDisabled?: boolean;
}

const AddAnalysisToMenu = ({ analysisId, isDisabled }: IProps) => {
  const dispatch = useAppDispatch();
  const isWorkspaceEnabled = useShowWorkspace();

  const {
    metadata: { status },
    settings: { chartSettings },
  } = useChartContext();
  const { chartType } = chartSettings;

  const { isReportInUrl, isDashboardInUrl } = useCurrentUrl();

  const { checkIsPermitted } = usePermission();

  // check permission for adding chart to Dashboard or creating a new dashboard
  const isPermittedToEditOrCreateDashboard = checkIsPermitted({
    requiredPermissionKeys: [
      PermissionKey.DashboardCreate,
      PermissionKey.DashboardEdit,
    ],
    validateKind: 'OR',
  });

  const showAddToWorkspace =
    chartType !== CHART_TYPES.PREDICTION &&
    chartType !== CHART_TYPES.DATA_TABLE &&
    isReportInUrl;

  const showAddToDashboard =
    status === AnalysisStatus.Success &&
    !isDashboardInUrl &&
    chartType !== CHART_TYPES.PREDICTION &&
    chartType !== CHART_TYPES.COHORT_SAMPLE;

  const openAddToWorkspaceModal = useCallback(() => {
    dispatch(
      openModal({
        type: ModalSliceType.AddToWorkspace,
        data: null,
        id: analysisId,
      }),
    );
  }, [analysisId, dispatch]);

  const openAddToDashboardModal = useCallback(() => {
    dispatch(
      openModal({
        type: ModalSliceType.AddToDashboard,
        data: null,
        id: analysisId,
      }),
    );
  }, [analysisId, dispatch]);

  const openAddToScratchpadModal = useCallback(() => {
    dispatch(
      openModal({
        type: ModalSliceType.AddToScratchpad,
        data: null,
        id: analysisId,
      }),
    );
  }, [analysisId, dispatch]);

  if (!showAddToDashboard && !(showAddToWorkspace && isWorkspaceEnabled)) {
    return null;
  }

  return (
    <SingleSelect
      trigger={
        <div>
          <KubitTooltip
            content={'You have view-only access and cannot add to dashboard.'}
            disabled={!isDisabled}
            placement="bottom"
          >
            <SecondaryButton
              isOutlined={true}
              disabled={isDisabled}
              trackContext="reports"
              trackAction="add to"
            >
              Add to
              <SvgIcon
                name="caret-down-fill"
                width={10}
                height={10}
                customFill="currentColor"
                isDisabled={isDisabled}
              />
            </SecondaryButton>
          </KubitTooltip>
        </div>
      }
      disabled={isDisabled}
    >
      {showAddToDashboard && (
        <PermissionElementWrapper
          isPermitted={isPermittedToEditOrCreateDashboard}
        >
          <SingleSelectOption
            label={'Dashboard'}
            onClick={openAddToDashboardModal}
            disabled={!isPermittedToEditOrCreateDashboard}
          />
        </PermissionElementWrapper>
      )}
      {showAddToWorkspace && isWorkspaceEnabled && (
        <SingleSelectOption
          label={'Workspace'}
          onClick={openAddToWorkspaceModal}
        />
      )}
      <SingleSelectOption
        label={'Scratchpad'}
        onClick={openAddToScratchpadModal}
      />
    </SingleSelect>
  );
};

export default AddAnalysisToMenu;
